import { createSlice } from "@reduxjs/toolkit";
import { getDiscountedPrice } from "utils/discounts.utils";

const initialState = {
  cart: [],
  cartTotal: 0, //this is original amount of items in cart
  amountAfterDiscount: 0, //this is amount after discount applied on itemsor loyalty points
  discountAmount: 0,
  itemsGrossAmount: 0, //this is amount after discount applied on items
  orderGrossAmount: 0, //this is amount after discount applied on items + delivery fee
  totalOrdersGrossAmount: 0,
  deliveryFee: 0,
  dineInServiceFee:{
    percentage:0,
    calculatedAmount:0,
  },
  orderSplReq: "",
  pickupVehicleInfo: "",
  pullAddress: true,
  deliveryChargesWaiverInfo: {
    isWaiver: false,
    waiverAmount: 0,
  },
  tipAmount: 0,
  taxAmount: 0,
  isSplitted: false,
  split: null,
  cartError: null,
  dineInPaymentOptions: null,
  orderId: null,
  orderNumber: null,
  dineInTableNumber: null,
  previousDiscountApplied:{},
  tableInfo:null,
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addItemToCart: (state, action) => {
      state.cart = [...state.cart, action.payload];
    },
    updateCartItem: (state, action) => {
      const { index, updatedItem } = action.payload;
      if (index >= 0 && index < state.cart.length) {
        state.cart[index] = updatedItem;
      }
    },
    setPickupVehicleInfo: (state, action) => {
      state.pickupVehicleInfo = action.payload;
    },

    setDiscountAmount: (state, action) => {
      state.discountAmount = action.payload;
    },
    removeItemFromCart: (state, action) => {
      state.cart = state.cart.filter((item) => item.id !== action.payload);
    },
    increaseItemQuantity: (state, action) => {
      const item = state.cart[action.payload.index];
      const discountOnItem = item?.discountOnItem
        ? JSON.parse(JSON.stringify(item?.discountOnItem))
        : {};
      if (item.isStockEnabled && item.quantity < item.availableStock) {
        item.quantity += 1;
        state.cartError = null;
      } else if (!item.isStockEnabled) {
        item.quantity += 1;
        state.cartError = null;
      } else {
        state.cartError = "Maximum available quantity reached";
        return; // Exit early if quantity cannot be increased
      }

      // Calculate discount with the new quantity
      // item.price is being saves as per item qunatity, we are multiplying the quantity with item price at runtime
      const actualPrice = discountOnItem.actualPrice;
      const discount = getDiscountedPrice({
        applicableDiscount: discountOnItem,
        itemPrice: actualPrice,
        quantity: item.quantity,
      });
      if (discount?.discountedPrice) {
        item.price = discount.discountedPrice;
      } else {
        item.price = actualPrice || item.price;
      }
      item.discountOnItem = {
        ...(item.discountOnItem || {}),
        ...(discount || {}),
      };
    },
    decreaseItemQuantity: (state, action) => {
      const item = state.cart[action.payload.index];
      const discountOnItem = item.discountOnItem
        ? JSON.parse(JSON.stringify(item.discountOnItem))
        : {};

      state.cartError = null;
      if (item.quantity > 1) {
        item.quantity -= 1;
      } else {
        state.cart.splice(action.payload.index, 1);
        return; // Exit early if item is removed
      }

      // item.price is being saves as per item qunatity, we are multiplying the quantity with item price at runtime
      const actualPrice = discountOnItem.actualPrice;
      const discount = getDiscountedPrice({
        applicableDiscount: discountOnItem,
        itemPrice: actualPrice,
        quantity: item.quantity,
      });
      if (discount?.discountedPrice) {
        item.price = discount.discountedPrice;
      } else {
        item.price = actualPrice || item.price;
      }
      item.discountOnItem = {
        ...(item.discountOnItem || {}),
        ...(discount || {}),
      };
    },
    clearCart: (state) => {
      state.cart = [];
      state.cartTotal = 0;
      state.amountAfterDiscount = 0;
      state.discountAmount = 0;
      state.tipAmount = 0;
      state.taxAmount = 0;
      state.split = null;
      state.isSplitted = false;
      state.orderId = null;
      state.orderNumber = null;
      state.dineInTableNumber = null;
      state.tableInfo = null;
    },
    updateCartPrice: (state, action) => {
      state.cartTotal = action.payload.originalPrice;
      state.amountAfterDiscount = action.payload.amountAfterDiscount;
      state.itemsGrossAmount = action.payload.itemsGrossAmount;
      state.orderGrossAmount = action.payload.orderGrossAmount;
      if (typeof action.payload.totalOrdersGrossAmount !== "undefined") {
        state.totalOrdersGrossAmount = action.payload.totalOrdersGrossAmount;
      }
      if (typeof action.payload.discountAmount !== "undefined") {
        state.discountAmount = action.payload.discountAmount;
      }
      if (typeof action.payload.tipAmount !== "undefined") {
        state.tipAmount = action.payload.tipAmount;
      }
      if (typeof action.payload.taxAmount !== "undefined") {
        state.taxAmount = action.payload.taxAmount;
      }
      if (typeof action.payload.dineInServiceFeeAmount !== "undefined") {
        state.dineInServiceFee = {
          ...state.dineInServiceFee,
          calculatedAmount: action.payload.dineInServiceFeeAmount,
        }
      }
    },
    removeDiscountAmountFromCartItems: (state) => {
      state.cart = state.cart.map((item) => ({
        ...item,
        price: item.discountOnItem?.actualPrice || item.price,
        discountOnItem: null,
      }))
    },

    setDeliveryFee: (state, action) => {
      state.deliveryFee = action.payload;
    },
    setdeliveryChargesWaiverInfo: (state, action) => {
      state.deliveryChargesWaiverInfo = action.payload;
    },
    setOrderSplreq: (state, action) => {
      state.orderSplReq = action.payload;
    },
    togglePullAddress: (state, action) => {
      state.pullAddress = action.payload;
    },
    setIsSplitted: (state, action) => {
      state.isSplitted = action.payload;
    },
    setSplit: (state, action) => {
      if (action.payload) {
        state.isSplitted = true;
      } else {
        state.isSplitted = false;
      }
      state.split = action.payload;
    },
    clearSplit: (state, action) => {
      state.split = null;
      state.isSplitted = false;
    },
    clearCartError: (state) => {
      state.cartError = null;
    },
    setCartError: (state, action) => {
      state.cartError = action.payload;
    },
    setDineInPaymentOptions: (state, action) => {
      state.dineInPaymentOptions = action.payload;
    },

    setExitingOrderDetails: (state, action) => {
      state.orderId = action.payload.orderId;
      state.orderNumber = action.payload.orderNumber;
      state.dineInTableNumber = action.payload.dineInTableNumber;
    },

    setPreviousDiscountApplied: (state, action) => {
      state.previousDiscountApplied = action.payload;
    },

    setDineInServiceFee: (state, action) => {
      state.dineInServiceFee = {
        ...state.dineInServiceFee,
        ...action.payload};
    },
    setTableInfo: (state, action) => {
      state.tableInfo = action.payload;
    },
  },
});

export const {
  setdeliveryChargesWaiverInfo,
  togglePullAddress,
  setPickupVehicleInfo,
  setOrderSplreq,
  setDeliveryFee,
  addItemToCart,
  removeItemFromCart,
  increaseItemQuantity,
  decreaseItemQuantity,
  clearCart,
  updateCartPrice,
  setDiscountAmount,
  setIsSplitted,
  setSplit,
  clearSplit,
  updateCartItem,
  clearCartError,
  setCartError,removeDiscountAmountFromCartItems,
  setDineInPaymentOptions,
  setExitingOrderDetails,
  setPreviousDiscountApplied,
  setDineInServiceFee,
  setTableInfo,
} = cartSlice.actions;
export default cartSlice.reducer;
